import { DatePipe } from '@angular/common';
import { Component, OnInit, Input } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormArray,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { FactoryService } from '@app/core/factories';
import {
  RFQ,
  QuotesService,
  RfqService,
  NegotiatedPrice,
  Quotation,
} from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';

@Component({
  selector: 'cnc-quotation-variant-form',
  templateUrl: './quotation-variant-form.component.html',
  styleUrls: ['./quotation-variant-form.component.scss'],
})
export class QuotationVariantFormComponent implements OnInit {
  pageTitle :string = this.translate.instant('TITLES.NEW_VARIANT_QUOTATION');
  @Input() quotation :Quotation;
  @Input() rfq :RFQ;
  @Input() variantQuotation :any;
  @Input() negotiatedPrice :NegotiatedPrice;

  url :string = environment.url;
  hide :boolean = true;
  loading :boolean = false;
  submitted :boolean = false;
  formData :UntypedFormGroup;
  filesToUpload :any[] = [];
  fileRemarks :any[] = [];
  tiered_pricing :UntypedFormArray;
  quotationAttachments :any[] = [];
  attachmentsToDelete :any[] = [];
  sysSettings :any = {};

  constructor(
    public activeModal :NgbActiveModal,
    private fb :UntypedFormBuilder,
    private router :Router,
    private quotesService :QuotesService,
    private rfqService :RfqService,
    private toastr :ToastrService,
    public settingsSvc :SettingsService,
    private translate :TranslateService,
    public factoryService :FactoryService,
    public authSvc :AuthService,
  ) {}

  ngOnInit() {
    this.getSettings();

    this.formData = this.fb.group({
      country: [
        { value: this.quotation.country, disabled: true },
        Validators.required,
      ],
      sample_price: [this.quotation.sample_price, Validators.required],
      sample_lead_time_days: [
        { value: this.quotation.sample_lead_time_days, disabled: true },
        Validators.required,
      ],
      expiry_dt: [
        { value: new Date(this.quotation.expiry_dt) },
        Validators.required,
      ],
      logistic_1: [
        { value: this.quotation.logistic_1, disabled: true },
        Validators.required,
      ],
      logistic_2: [{ value: this.quotation.logistic_2, disabled: true }],
      lead_time_days_log1: [
        { value: this.quotation.lead_time_days_log1, disabled: true },
        Validators.required,
      ],
      lead_time_days_log2: [
        { value: this.quotation.lead_time_days_log2, disabled: true },
      ],
      default_photo: ['0'],
      factory_id: [
        { value: this.quotation.factory_id, disabled: true },
        Validators.required,
      ],
      fileremarks: this.fb.array([]),
      tiered_pricing: this.fb.array([]),
      notes: [''],
    });

    this.addTieredPricing();
    this.addTieredPricing();
    this.addTieredPricing();
    this.addTieredPricing();
    this.addTieredPricing();

    if (!this.rfq) {
      this.rfq = this.rfqService.getActiveRfq();
    }

    const variantsGroup = {};
    if (!this.variantQuotation) {
      Object.keys(this.quotation.variants_influence_price).forEach((key) => {
        if (this.quotation.variants_influence_price[key].length > 0) {
          variantsGroup[key] = ['', Validators.required];
        }
      });
      // this.rfq.product_variants.forEach((variant: any) => {
      //     variantsGroup[variant.variant_name] = ["", Validators.required];
      // });
    } else {
      Object.keys(this.quotation.variants_influence_price).forEach((key) => {
        if (this.quotation.variants_influence_price[key].length > 0) {
          if (this.variantQuotation.variants[key]) {
            variantsGroup[key] = this.variantQuotation.variants[key];
          } else {
            variantsGroup[key] = ['', Validators.required];
          }
        }
      });
      // this.rfq.product_variants.forEach((variant: any) => {
      //     if (this.variantQuotation.variants[variant.variant_name]) {
      //         variantsGroup[variant.variant_name] = this.variantQuotation.variants[variant.variant_name];
      //     } else {
      //         variantsGroup[variant.variant_name] = ["", Validators.required];
      //     }
      // });
    }
    this.formData.addControl('variants', this.fb.group(variantsGroup));

    if (this.variantQuotation) {
      const fileremarks = this.formData.get('fileremarks') as UntypedFormArray;
      this.quotesService.setActive(this.quotation);
      this.quotesService
        .getQuotationAttachments(this.variantQuotation.id)
        .subscribe((files :any[]) => {
          this.quotationAttachments = files;
          files.forEach((file :any, idx) => {
            if (this.quotation.photo == file.filename) {
              this.formData.patchValue({ default_photo: idx.toString() });
            }
            this.filesToUpload.push({
              previewSrc: this.url + '/file?f=' + file.filename,
            });
            fileremarks.push(this.fb.control(file.remarks));
          });
        });
      this.pageTitle = this.translate.instant('TITLES.UPDATE_VARIANT_QUOTATION');

      this.formData.patchValue({
        sample_price: this.variantQuotation.sample_price,
        notes: this.variantQuotation.notes,
      });
      // this.formData.patchValue(Object.assign(this.quotation, this.variantQuotation));

      if (this.variantQuotation.expiry_dt) {
        const datePipe = new DatePipe('en');
        const date = datePipe.transform(
          this.variantQuotation.expiry_dt,
          'yyyy-MM-dd',
        );
        this.formData.get('expiry_dt').patchValue(date);
      }

      const tiered_pricing = this.formData.get(
        'tiered_pricing',
      ) as UntypedFormArray;
      if (
        this.variantQuotation.tiered_pricing_json &&
        this.variantQuotation.tiered_pricing_json.length > 0
      ) {
        this.variantQuotation.tiered_pricing_json.forEach((tp, k) => {
          if (tp.logistic_1_payment_term) {
            tp.logistic_1_payment_term = this.sysSettings.payment_terms.find(
              (row) => {
                return row.label == tp.logistic_1_payment_term;
              },
            );
          }
          if (tp.logistic_1_markup) {
            tp.logistic_1_markup = this.sysSettings.mark_up_structure.find(
              (row) => {
                return row.label == tp.logistic_1_markup;
              },
            );
          }
          if (tp.logistic_2_payment_term) {
            tp.logistic_2_payment_term = this.sysSettings.payment_terms.find(
              (row) => {
                return row.label == tp.logistic_2_payment_term;
              },
            );
          }
          if (tp.logistic_2_markup) {
            tp.logistic_2_markup = this.sysSettings.mark_up_structure.find(
              (row) => {
                return row.label == tp.logistic_2_markup;
              },
            );
          }
          tiered_pricing.at(k).patchValue(tp);
        });
      }
    }
  }

  setPaymentMarkup(idx) {
    const tiered_pricing = this.formData.get('tiered_pricing') as UntypedFormArray;
    const value = this.formData.value.tiered_pricing[idx];

    // logistics 1
    const logistic_1_amount = value.quantity * value.logistic_1;
    this.sysSettings.payment_terms.forEach((pt :any) => {
      if (logistic_1_amount >= pt.min_value && value.quantity <= pt.max_value) {
        tiered_pricing.at(idx).patchValue({ logistic_1_payment_term: pt });
      }
    });
    this.sysSettings.mark_up_structure.forEach((mk :any) => {
      if (logistic_1_amount >= mk.min_value && value.quantity <= mk.max_value) {
        tiered_pricing.at(idx).patchValue({ logistic_1_markup: mk });
      }
    });

    // logistic_2
    if (this.quotation.logistic_2 && this.quotation.logistic_2 != '') {
      const logistic_2_amount = value.quantity * value.logistic_2;
      this.sysSettings.payment_terms.forEach((pt :any) => {
        if (logistic_2_amount >= pt.min_value && value.quantity <= pt.max_value) {
          tiered_pricing.at(idx).patchValue({ logistic_2_payment_term: pt });
        }
      });
      this.sysSettings.mark_up_structure.forEach((mk :any) => {
        if (logistic_2_amount >= mk.min_value && value.quantity <= mk.max_value) {
          tiered_pricing.at(idx).patchValue({ logistic_2_markup: mk });
        }
      });
    }
  }

  getSettings() {
    this.sysSettings = this.settingsSvc.getSettingsValue();
    if (!this.sysSettings.payment_terms) {
      setTimeout(() => {
        this.getSettings();
      }, 1000);
    }
  }

  updateNegCountry() {
    this.factoryService.factory$.subscribe((factories :any) => {
      const factory = factories.find((row) => {
        return row.id == this.formData.value.factory_id;
      });
      this.formData.patchValue({ country: factory.country });
    });
  }

  addTieredPricing() {
    this.tiered_pricing = this.formData.get('tiered_pricing') as UntypedFormArray;
    this.tiered_pricing.push(
      this.fb.group({
        quantity: [0, Validators.required],
        logistic_1: [0, Validators.required],
        logistic_1_payment_term: [],
        logistic_1_markup: [],
        logistic_2: [0, Validators.required],
        logistic_2_payment_term: [],
        logistic_2_markup: [],
      }),
    );
  }

  handleFileInput(event :any) {
    const files = Array.prototype.slice.call(event.target.files);
    // this.filesToUpload = this.filesToUpload.concat(files);
    const fileremarks = this.formData.get('fileremarks') as UntypedFormArray;

    const kani = this;
    let maxSizeError = false;

    files.forEach((file, idx) => {
      if (file.size > 10000000) {
        maxSizeError = true;
      } else {
        const ftu :any = {
          previewSrc: '',
          file: file,
        };

        this.filesToUpload.push(ftu);

        fileremarks.push(this.fb.control(' '));

        if (file.type.toString().search('image') >= 0) {
          const index = this.filesToUpload.length - 1;
          const fReader = new FileReader();
          fReader.readAsDataURL(file);
          fReader.onload = function (e :any) {
            kani.filesToUpload[index].previewSrc = e.target.result;
          };
        }
      }
    });

    if (maxSizeError) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });
    }

    event.target.value = '';
  }

  removeFile(idx) {
    const fileremarks = this.formData.get('fileremarks') as UntypedFormArray;
    fileremarks.removeAt(idx);
    this.filesToUpload.splice(idx, 1);
    if (this.quotationAttachments[idx]) {
      this.attachmentsToDelete.push(this.quotationAttachments[idx].filename);
    }
  }

  onSubmit(status ?:number) {
    this.submitted = true;
    if (this.formData.invalid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    const data = this.formData.value;
    const tiered_pricing = [];
    data.tiered_pricing.forEach((tp, k) => {
      if (tp.quantity > 0) {
        const tpRow :any = {
          quantity: tp.quantity,
          logistic_1: tp.logistic_1,
          logistic_1_payment_term: tp.logistic_1_payment_term.label,
          logistic_1_markup: tp.logistic_1_markup.label,
          logistic_1_downpayment_percent: tp.logistic_1_payment_term.min_p,
          logistic_1_finalpayment_percent: tp.logistic_1_payment_term.max_p,
          logistic_1_markup_percent: tp.logistic_1_markup.markup,

          logistic_2: tp.logistic_2,
        };
        if (tp.logistic_2 && tp.logistic_2 != '') {
          tpRow.logistic_2_payment_term = tp.logistic_2_payment_term.label;
          tpRow.logistic_2_markup = tp.logistic_2_markup.label;
          tpRow.logistic_2_downpayment_percent = tp.logistic_2_payment_term.min_p;
          tpRow.logistic_2_finalpayment_percent = tp.logistic_2_payment_term.max_p;
          tpRow.logistic_2_markup_percent = tp.logistic_2_markup.markup;
        }
        tiered_pricing.push(tpRow);
      }
    });
    // data.tiered_pricing.forEach((tp, k) => {
    //     if (tp.quantity > 0) {
    //         tiered_pricing.push(tp);
    //     }
    // });

    Object.keys(data.variants).forEach((v) => {
      if (data.variants[v] == '') {
        delete data.variants[v];
      }
    });

    if (Object.keys(data.variants).length == 0) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.QUOTATION_VARIANT.TITLE'),
        text: this.translate.instant('MESSAGES.QUOTATION_VARIANT.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    this.loading = true;

    const fd = new FormData();
    fd.append('parent_id', this.quotation.id.toString());
    fd.append('notes', data.notes);
    fd.append('variants', JSON.stringify(data.variants));

    fd.append('rfq_id', this.rfq.id.toString());
    fd.append('country', this.quotation.country);
    fd.append('factory_id', this.quotation.factory_id.toString());
    fd.append('sample_price', data.sample_price);
    fd.append(
      'sample_lead_time_days',
      this.quotation.sample_lead_time_days.toString(),
    );
    fd.append('default_photo', data.default_photo);
    fd.append('tiered_pricing', JSON.stringify(tiered_pricing));
    fd.append('logistic_1', this.quotation.logistic_1);
    fd.append('logistic_2', this.quotation.logistic_2);
    fd.append('lead_time_days_log1', this.quotation.lead_time_days_log1.toString());
    fd.append('lead_time_days_log2', this.quotation.lead_time_days_log2.toString());
    fd.append('expiry_dt', this.quotation.expiry_dt);
    data.fileremarks.forEach((fileremark, idx) => {
      if (this.filesToUpload[idx].file) {
        fd.append('file[]', this.filesToUpload[idx].file);
      }
    });
    fd.append('file_remarks', JSON.stringify(data.fileremarks));
    if (
      status &&
      status > 0 &&
      (!this.variantQuotation || this.variantQuotation.status < 2)
    ) {
      fd.append('status', status.toString());
    }
    if (this.variantQuotation && this.variantQuotation.id) {
      fd.append(
        'existing_file_count',
        (
          this.quotationAttachments.length - this.attachmentsToDelete.length
        ).toString(),
      );
      fd.append('attachments_to_delete', this.attachmentsToDelete.join(','));
      this.quotesService.updateQuotation(this.variantQuotation.id, fd).subscribe(
        (resp :any) => {
          if (resp.record.status > 1 && this.variantQuotation.status == 1) {
            this.rfq.quotations_count += 1;
            this.rfqService.updateRowData(this.rfq);
          }
          if (this.negotiatedPrice) {
            const q = resp.record;
            q.price_neg = 1;
            this.quotesService.updateRowData(q);
          }
          this.toastr.success(
            this.translate.instant('MESSAGES.UPDATE_SUCCESS.TEXT'),
            this.translate.instant('MESSAGES.UPDATE_SUCCESS.TITLE'),
          );
          this.activeModal.close(resp.record);
        },
        (errorResp) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
            text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
            type: 'error',
            confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
          });
        },
      );
    } else {
      this.quotesService.createQuotation(fd).subscribe(
        (resp :any) => {
          if (resp.record.status > 1) {
            this.rfq.quotations_count += 1;
            this.rfqService.updateRowData(this.rfq);
          }
          this.toastr.success(
            this.translate.instant('MESSAGES.CREATE_SUCCESS.TEXT'),
            this.translate.instant('MESSAGES.CREATE_SUCCESS.TITLE'),
          );
          this.activeModal.close(resp.record);
          // this.router.navigateByUrl(`quotes/quotation-requests/${this.rfq.id}/quotations`);
        },
        (errorResp) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
            text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
            type: 'error',
            confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
          });
        },
      );
    }
  }
}
